import { useLoaderData, useLocation } from "@remix-run/react";
import WvBaseCategoryPage from "~/components/templates/WvBaseCategoryPage";
import WvBlogContentPage from "~/components/templates/WvBlogContentPage";
import WvCustomContentPage from "~/components/templates/WvCustomContentPage";
import WvProductListPage from "~/components/templates/WvProductListPage";
import Wv404Page from "~/components/templates/Wv404Page";
import type { NotFound404PageDocument } from "prismicio-types";
import type { ActionFunctionArgs, MetaFunction } from "@remix-run/node";
import { useEffect, useMemo } from "react";
import * as Sentry from "@sentry/remix";
import WvProductPage from "~/components/templates/WvProductPage";
import { notifyMe } from "@commercekit/api/lib/endpoints/notifyMe.server";
import { NotifyMeAction } from "@commercekit/core";
import {
  AuthActions,
} from "@commercekit/remix";
import {
  initAction,
  signupAction,
} from "@commercekit/remix/lib.server";
import { validateCaptcha } from "~/routes-server/captcha.server";
import { loader } from "~/routes-server/$slug/index.server";
import type {
  CustomContentPageResultType,
  BlogContentPageResultType,
  ProductPageResultType,
  CategoryPageResultType,
} from "~/routes-server/$slug/types";
import { metaHelper } from "~/lib/meta";

export type { headers } from "@commercekit/remix/lib.server";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const arg = data == null ? { data: undefined } : { data };
  const base = metaHelper(arg) ?? [];
  const schemas: Array<{ "script:ld+json": Record<string, unknown> }> = [];
  if (data == null) return base;
  if ("collectionPageSchema" in data && data.collectionPageSchema != null) {
    schemas.push({ "script:ld+json": data.collectionPageSchema });
  }
  if ("breadcrumbSchema" in data && data.breadcrumbSchema != null) {
    schemas.push({ "script:ld+json": data.breadcrumbSchema });
  }
  if ("itemListSchema" in data && data.itemListSchema != null) {
    schemas.push({ "script:ld+json": data.itemListSchema });
  }
  if ("productSchema" in data && data.productSchema != null) {
    schemas.push({ "script:ld+json": data.productSchema });
  }
  if ("videoSchema" in data && data.videoSchema != null) {
    schemas.push({ "script:ld+json": data.videoSchema });
  }
  if ("faqSchema" in data && data.faqSchema != null) {
    schemas.push({ "script:ld+json": data.faqSchema });
  }
  if ("webPageSchema" in data && data.webPageSchema != null) {
    schemas.push({ "script:ld+json": data.webPageSchema });
  }
  if ("shippingServiceSchema" in data && data.shippingServiceSchema != null) {
    schemas.push({ "script:ld+json": data.shippingServiceSchema });
  }
  return [...base, ...schemas];
};
export { default as ErrorBoundary } from "~/components/atoms/GeneralErrorBoundary";
export { loader };

export const action = async (args: ActionFunctionArgs) => {
  const helper = await initAction(args);
  switch (helper.action) {
    case AuthActions.SIGNUP: {
      const recaptchaValue = helper.formData.get("recaptchaValue") as string;
      const captchaResponse = await validateCaptcha(recaptchaValue);
      if (captchaResponse.success) {
        const [errors] = await signupAction(helper);
        if (errors) return helper.error(errors);
      } else return helper.error(["invalid_captcha"]);
      break;
    }
    case NotifyMeAction.NOTIFY_ME: {
      const { request, error, session, formData } = helper;

      const recaptchaValue = formData.get("recaptchaValue") as string;
      const input: { email: string; sku: string } = {
        email: (formData.get("email") as string) || "",
        sku: (formData.get("sku") as string) || "",
      };
      if (recaptchaValue && input.email) {
        const captchaResponse = await validateCaptcha(recaptchaValue);
        if (captchaResponse.success) {
          const [errors] = await notifyMe(request, input, session.getToken());
          if (errors) return error(errors.map((i) => i.message));
        }
      }
    }
  }
  return helper.response();
};

export const BasePage = () => {
  const { pathname } = useLocation();
  const { canonicalsUrl, currentPage, result } = useLoaderData<typeof loader>();

  const currentRenderedPage = useMemo(() => currentPage, [currentPage]);

  // PDP, PLP and CMS pages share this route; the tag tells them apart in Sentry.
  useEffect(() => {
    Sentry.setTag("page_type", currentRenderedPage);
    return () => Sentry.setTag("page_type", undefined);
  }, [currentRenderedPage]);

  if (currentRenderedPage === "NOT_FOUND_PAGE") {
    return (
      <Wv404Page
        page={
          (result as { notFoundPage: NotFound404PageDocument | null })
            .notFoundPage
        }
      />
    );
  }

  switch (currentRenderedPage) {
    case "CUSTOM_CONTENT_PAGE": {
      const customResult = result as CustomContentPageResultType;
      return (
        <>
          <WvCustomContentPage customContentPageResult={customResult} />
        </>
      );
    }
    case "BLOG_CONTENT_PAGE": {
      return (
        <WvBlogContentPage
          blogContentPageResult={result as BlogContentPageResultType}
        />
      );
    }
    case "PRODUCT_DETAILS_PAGE": {
      const { captchaSiteKey } = result as ProductPageResultType;
      return (
        <WvProductPage
          key={pathname}
          productPageResult={result as ProductPageResultType}
          canonicalsUrl={canonicalsUrl}
          captchaSiteKey={captchaSiteKey}
        />
      );
    }
    case "CATEGORY_PAGE": {
      const { categoryData } = result as CategoryPageResultType;
      return (
        <>
          {categoryData &&
          categoryData?.children &&
          categoryData?.children?.length > 0 ? (
            <WvBaseCategoryPage
              categoryPageResult={result as CategoryPageResultType}
            />
          ) : (
            <WvProductListPage
              categoryPageResult={result as CategoryPageResultType}
            />
          )}
        </>
      );
    }
  }
};

export default BasePage;
